import { setTabIndex } from './tabindex';

export default class HeaderPanels {
  constructor() {
    this.debug = false; // Set to true to enable debug logging.
    this.eventPrefix = 'panel';
    this.panelIds = [];
    this.activePanels = [];
    this.navigationLocations = []; // This will be used for closing panels within the same navigation.
    this.lastInputType = null; // 'click' or 'keydown.
    this.panelHeights = []; // Store heights of opened panels.

    // Define selectors for focusable elements within panels.
    this.focusableSelectors = [
      '.denhaag-header__back-button',
      '.denhaag-header__close-button',
      '.denhaag-header__link',
      'a[href]',
      'button:not([disabled])',
      '[tabindex="0"]',
      '[data-toggle-panel]'
    ];
  }

  /**
   * Initialize the HeaderPanels component.
   */
  initialize() {
    // Keep track of the last input type (keyboard or click), don`t use `event.preventDefault()` here.
    document.addEventListener( 'keydown', (e) => this.lastInputType = 'keydown' );
    document.addEventListener( 'mousedown', () => this.lastInputType = 'click' );

    this.navigationLocations = [document.querySelector( '.denhaag-header__primary' ), document.querySelector( '.denhaag-header__secondary' )].filter( Boolean );

    // Filtering out the mobile menu toggle here, as that is handled separately.
    [...document.querySelectorAll( '[data-toggle-panel]' )]
      ?.filter( t => t.id !== 'denhaag-menu-toggle' )
      ?.forEach( (toggle) => {
        const panelId = toggle.getAttribute( 'data-toggle-panel' );
        if (panelId) {
          this.setPanelIds( panelId );
          toggle.addEventListener( 'click', (e) => {
            e.preventDefault();
            this.dispatchEvent( "toggle", {id: panelId} )
          } );

          toggle.addEventListener( 'keydown', (e) => {
            const {key, shiftKey} = e;
            switch (key) {
              case 'Enter':
              case 'Space':
                // Pressing space or enter on the toggle button should toggle the panel.
                e.preventDefault();
                this.dispatchEvent( "toggle", {id: panelId} )
                break;

              case 'Tab':
                if (!this.isPanelActive( panelId ) || 1 >= this.activePanels.length) {
                  // Do nothing if the panel is not active or there's only one active panel.
                  break;
                }

                const currentPanel = document.getElementById( panelId );
                // If Shift+Tab is pressed on the first focusable element, move focus to the last focusable element of the previous active panel.
                // If Tab is pressed on the last focusable element, move focus to the first focusable element of the previous active panel.
                if (toggle === (shiftKey ? this.getFirstFocusableChild( currentPanel ) : this.getLastFocusableChild( currentPanel ))) {
                  e.preventDefault();
                  setTimeout( () => this.setFocusElement( currentPanel, !shiftKey ), 1 );
                }
                break;

              case 'ArrowUp':
              case 'ArrowLeft':
                if (this.isPanelActive( panelId )) {
                  e.preventDefault();
                  this.dispatchEvent( "close", {id: panelId} );
                }
                break;

              case 'Escape':
                // Close the last active panel on Escape key press.
                if (this.isPanelActive( this.activePanels[this.activePanels.length - 1] )) {
                  e.preventDefault();
                  this.dispatchEvent( "close", {id: this.activePanels[this.activePanels.length - 1]} );
                }
                break;

              case 'ArrowRight':
              case 'ArrowDown':
                if (!this.isPanelActive( panelId )) {
                  e.preventDefault();
                  this.dispatchEvent( "open", {id: panelId} );
                }
                break;

              case 'Home':
                if (this.isPanelActive( panelId )) {
                  // Focus the first focusable element in the panel.
                  e.preventDefault();
                  this.setFocusElement( panelId, true );
                }
                break;

              case 'End':
                if (this.isPanelActive( panelId )) {
                  // Focus the last focusable element in the panel.
                  e.preventDefault();
                  this.setFocusElement( panelId, false );
                }
                break;
            }
          } );
        }
      } );

    // Add keyboard support for links in the navigation to trigger click on Space key press.
    this.navigationLocations?.forEach( (navLocation) => {
      const links = navLocation.getElementsByTagName( 'A' );
      [...links]?.forEach( link => link.addEventListener( 'keydown', (e) => {
        switch (e.code) {
          case 'Space':
            e.preventDefault();
            link.click();
            break;

          case 'Escape':
            if (this.activePanels.length) {
              e.preventDefault();
              this.dispatchEvent( "close", {id: this.activePanels[this.activePanels.length - 1]} );
            }
            break;

          case 'Home':
            if (this.isPanelActive( this.activePanels[this.activePanels.length - 1] )) {
              // Focus the first focusable element in the panel.
              e.preventDefault();
              this.setFocusElement( this.activePanels[this.activePanels.length - 1], true );
            }
            break;

          case 'End':
            if (this.isPanelActive( this.activePanels[this.activePanels.length - 1] )) {
              // Focus the last focusable element in the panel.
              e.preventDefault();
              this.setFocusElement( this.activePanels[this.activePanels.length - 1], false );
            }
            break;
        }
      } ) );

      // Close the open panels once keyboard focus has genuinely left this navigation.
      //
      // DHNL-6: this used to also test `event.relatedTarget`, which Safari reports as `null`
      // far more often than Chrome does — and `navLocation.contains( null )` is `false`, so
      // that test silently passed and left a 10ms timer as the only guard. Focus had often
      // not landed yet, closing the panels mid-tab and resetting their tabindexes under the
      // user. Wait for the focus change to actually be applied and judge on
      // `document.activeElement` alone, which is reliable once it has settled.
      navLocation?.addEventListener( 'focusout', () => requestAnimationFrame( () => requestAnimationFrame( () => {
        const {activeElement} = document;

        // Nothing holds focus yet: Safari parks on `<body>` between transitions, and focus
        // may have left the document entirely. Leaving the panels open is safer than
        // wrongly tearing them down.
        if (!activeElement || activeElement === document.body) {
          return;
        }

        if (this.lastInputType === 'keydown' && this.activePanels.length && !navLocation.contains( activeElement )) {
          this.dispatchEvent( "closeAll", {focus: false} );
        }
      } ) ) );
    } );

    this.setPanelHeights();
    this.resize();
  }

  /**
   * Set up event listeners for panel actions.
   */
  setupEventListeners() {
    window.addEventListener( `${this.eventPrefix}:toggleDebug`, () => this.debug = !this.debug );
    window.addEventListener( `${this.eventPrefix}:enableDebug`, () => this.debug = true );
    window.addEventListener( `${this.eventPrefix}:disableDebug`, () => this.debug = false );
    window.addEventListener( `${this.eventPrefix}:toggle`, (e) => this.togglePanel( e?.detail?.id ) );
    window.addEventListener( `${this.eventPrefix}:open`, (e) => this.openPanel( e?.detail?.id ) );
    window.addEventListener( `${this.eventPrefix}:close`, (e) => this.closePanel( e?.detail?.id, true ) );
    window.addEventListener( `${this.eventPrefix}:closeAll`, (e) => {
      let {focus} = e?.detail || {};

      if (typeof focus !== 'boolean') {
        // Set default focus behavior to true.
        focus = false;
      }

      this.debug && console.info( this.activePanels );

      if (!this.activePanels.length) {
        this.debug && console.info( 'No active panels to close' );
        return;
      }

      // Close all active panels.
      this.activePanels?.forEach( id => this.closePanel( id, focus ) );
    } );
    window.addEventListener( `${this.eventPrefix}:setHeights`, () => this.setPanelHeights( true ) );
    window.addEventListener( `${this.eventPrefix}:setHeight`, (e) => this.setPanelHeight( e?.detail?.id ) );
    window.addEventListener( `${this.eventPrefix}:updateHeight`, (e) => this.setPanelHeights( e?.detail?.refresh || false, e?.detail?.id || null ) );
  }

  /**
   * Dispatch a custom event with optional data.
   *
   * @param {string} name The name of the event to dispatch.
   * @param {Object} data Optional data to include with the event.
   */
  dispatchEvent(name, data = {}) {
    if (!name) {
      return;
    }

    this.debug && console.group( `Dispatching event: ${name}` );
    this.debug && console.info( data );

    // Split name on `:` to add prefix if missing. (internal or external event).
    if( name.indexOf( ':' ) === -1 ) {
      name = `${this.eventPrefix}:${name}`;
    }

    window.dispatchEvent( new CustomEvent( name, {
      detail: data,
      bubbles: true
    } ) );

    this.debug && console.groupEnd();
  }

  /**
   * Handle window resize events to close panels if necessary.
   */
  resize() {
    const mobileMenuToggle = document.getElementById( 'denhaag-menu-toggle' )
    if (!mobileMenuToggle) {
      return;
    }

    this.debug && console.info( 'Resize event detected', this.isElementVisible( mobileMenuToggle ) );

    if (this.isElementVisible( mobileMenuToggle )) {
      this.dispatchEvent( "closeAll" );
    }

    this.setPanelHeights( true );
  }

  /**
   * Check if an element is visible in the DOM.
   *
   * @param {HTMLElement} el The element to check.
   * @return {boolean} True if the element is visible, false otherwise.
   */
  isElementVisible(el) {
    const style = window.getComputedStyle( el );
    return style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0';
  }

  /**
   * Add a panel ID to the list if it's not already present.
   *
   * @param {string} id The ID of the panel to add.
   */
  setPanelIds(id) {
    if (this.panelIds.includes( id )) {
      return;
    }
    this.panelIds.push( id );
  }

  /**
   * Set the heights of all panels.
   * We temporarily clone all panels to measure their heights accurately.
   *
   * {boolean} refresh Whether to refresh the panel heights.
   * {string|null} panelId Optional specific panel ID to update.
   */
  setPanelHeights( refresh = false, panelId = null ) {
    this.debug && console.group( 'setPanelHeights', {refresh, panelId} );

    if (refresh || !this.panelHeights.length) {
      this.panelHeights = [];
    }

    if( !this.panelIds?.length ) {
      this.debug && console.warn( 'No panel IDs found to set heights for.' );
      this.debug && console.groupEnd();
      return;
    }

    if ( !! panelId && this.panelIds.includes( panelId )) {
      // 1. Calculate height for a specific panel only.

      // Check if the panel height is already calculated.
      const existingIndex = this.panelHeights.findIndex( item => item.id === panelId );

      const panel = document.getElementById( panelId );
      const measureElement = panel.querySelector( '.denhaag-header__panel' ) || panel;

      let calculatedHeight = this.calculatePanelHeight( measureElement );

      if (existingIndex !== -1) {
        // 1A. Update existing entry.
        if (this.panelHeights[existingIndex]?.parent) {
          calculatedHeight -= 10; // Small offset to prevent showing the parent below.
        }

        this.panelHeights[existingIndex] = {
          id: panelId,
          height: calculatedHeight,
          parent: this.findParentPanel( panelId ),
        };
      } else {
        // 1B. Add new entry.
        const parent = this.findParentPanel( panelId );
        if (parent) {
          calculatedHeight -= 10; // Small offset to prevent showing the parent below.
        }

        this.panelHeights.push( {
          id: panelId,
          height: calculatedHeight,
          parent: parent,
        } );
      }
    } else if (!panelId) {
      // 2. Calculate heights for all panels.

      this.panelIds?.forEach( (panelId) => {
        const panel = document.getElementById( panelId );

        // If panel not found, skip.
        if (!panel) {
          this.debug && console.warn( `Panel with ID ${panelId} not found.` );
          return;
        }

        const measureElement = panel?.querySelector( '.denhaag-header__panel' ) || panel;
        const parent = this.findParentPanel( panelId );

        let calculatedHeight = this.calculatePanelHeight( measureElement );

        if (parent) {
          calculatedHeight -= 10; // Small offset to prevent showing the parent below.
        }

        this.panelHeights.push( {
          id: panelId,
          height: calculatedHeight,
          parent: parent,
        } );
      } );
    }

    this.debug && console.info( this.panelHeights );
    this.debug && console.groupEnd();
  }

  /**
   * Clone a panel element and measure its height.
   *
   * @param {HTMLElement} panel The panel element to clone and measure.
   * @return {number} The measured height of the cloned panel.
   */
  cloneAndMeasurePanel(panel) {
    const clone = panel.cloneNode( true );
    clone.style.cssText = `position: absolute !important;visibility: hidden !important;height: auto !important;max-height: none !important;min-height: 0 !important;display: block !important;left: -9999px !important;`;

    document.body.appendChild( clone );
    const height = clone.offsetHeight;
    document.body.removeChild( clone ); // DO NOT FORGET to remove the clone!

    return height;
  }

  /**
   * Calculate the full panel height including CSS variable offsets.
   *
   * @param {HTMLElement} measureElement The element to measure.
   * @return {number} The calculated height in pixels.
   */
  calculatePanelHeight(measureElement) {
    if (!measureElement || !(measureElement instanceof HTMLElement)) {
      return 0;
    }

    return this.cloneAndMeasurePanel( measureElement )
      + this.getCSSVariable( '--denhaag-header-height', 0 )
      + this.getCSSVariable( '--denhaag-header-panel-container-row-gap', 0 )
      + this.getCSSVariable( '--denhaag-header-panel-bar-height', 0 )
      + (this.getCSSVariable( '--denhaag-header-panel-padding-block', 0 ) * 1.5);
  }

  /**
   * Find the parent panel ID of a given panel ID.
   *
   * @param {string} id The ID of the panel to find the parent for.
   * @return {unknown}
   */
  findParentPanel(id) {
    const parentId = this.panelIds?.find( (panelId) => {
      const panel = document.getElementById( panelId );
      return !!(panel && panel.contains( document.getElementById( id ) ) && panelId !== id);
    } );

    return parentId || null;
  }

  /**
   * Check if a panel is active by its ID.
   *
   * @param {string} id The ID of the panel to check.
   * @returns {boolean} True if the panel is active, false otherwise.
   */
  isPanelActive(id) {
    if( ! this.activePanels?.length ) {
      return false;
    }

    return this.activePanels.includes( id );
  }

  /**
   * Toggle a panel by its ID.
   *
   * @param {string} id The ID of the panel to toggle.
   */
  togglePanel(id) {
    if (!this.panelIds.includes( id )) {
      // Do nothing if the panel doesn't exist.
      return;
    }

    this.isPanelActive( id )
      ? this.dispatchEvent( 'close', {id: id} )
      : this.dispatchEvent( 'open', {id: id} );
  }

  /**
   * Open a panel by its ID.
   *
   * @param {string} id The ID of the panel to open.
   */
  openPanel(id) {
    const panel = document.getElementById( id )
    if (!panel) {
      // Do nothing if the panel doesn't exist.
      return;
    }

    const {dataset} = panel;

    // Close mobile menu if it's open when opening a header panel.
    window.dispatchEvent( new CustomEvent( "mobileMenu:close", {bubbles: true} ) );

    if (this.activePanels.length) {
      this.closePanelsFromSameDepth( dataset?.panelDepth || undefined );
      this.activePanels?.forEach( activePanelId => this.updateTabIndexPerPanel( activePanelId, false ) );

      // This ensures that when you open a panel in the primary navigation, any open panels from the secondary navigation will be automatically closed, and vice versa.
      const newPanelNavLocation = this.navigationLocations?.find( navLocation => navLocation?.contains( panel ) );

      if (newPanelNavLocation) {
        this.activePanels.filter( activePanelId => {
          const activePanel = document.getElementById( activePanelId );
          return activePanel && !newPanelNavLocation.contains( activePanel );
        } )?.forEach( activePanelId => this.closePanel( activePanelId, false ) );
      }
    }

    // Add the panel ID to the active panels array if it's not already present.
    this.activePanels.push( id );

    const activeClass = dataset.togglePanelClass || 'is-active';
    panel.classList.add( activeClass );

    this.updateToggles( id, true );
    this.updateTabIndexPerPanel( panel, true );

    // Use requestAnimationFrame to measure AFTER the browser has applied the CSS.
    requestAnimationFrame(() => this.setPanelHeight( panel ));

    if ('keydown' === this.lastInputType && parseInt( dataset.panelDepth ) > 0) {
      this.setFocusElement( panel, true );
    }
  }

  /**
   * Set the panel height CSS variable based on the panel's dimensions.
   *
   * @param {HTMLElement|string} panel The panel element or its ID.
   */
  setPanelHeight(panel) {
    this.debug && console.group( 'setPanelHeight' );

    if (typeof panel === 'string') {
      panel = document.getElementById( panel );
    }

    if (!panel) {
      this.debug && console.warn( 'Panel not found' );
      this.debug && console.groupEnd();
      return;
    }

    const {id} = panel;
    const measureElement = panel.querySelector( '.denhaag-header__panel' ) || panel;

    // Force reflow.
    void measureElement.offsetHeight;

    this.debug && console.info( 'Setting panel height for ID:', id );
    this.debug && console.info( this.panelHeights );

    // Get the current panel data.
    let currentPanelIndex = this.panelHeights.findIndex( item => item.id === id );

    if (currentPanelIndex === -1) {
      this.dispatchEvent( "setHeights" );

      this.debug && console.warn( 'Current panel not found in panelHeights' );
      this.debug && console.groupEnd();
      return;
    }
    let currentPanel = this.panelHeights[currentPanelIndex];

    if( !currentPanel ) {
      this.debug && console.warn( 'Current panel data is undefined' );
      this.debug && console.groupEnd();
      return;
    }

    const heightValue = currentPanel.height;

    // If there's no parent, just set the height on this panel
    if (!currentPanel.parent) {
      document.body.style.setProperty( '--header-panel-height', `${currentPanel.height}px` );

      this.debug && console.info( `Set height on panel #${id}: ${currentPanel.height}px` );
      this.debug && console.groupEnd();
      return;
    }

    // Apply height to all parent panels
    while (currentPanel) {
      const panelElement = document.getElementById( currentPanel.id );

      if( ! panelElement ) {
        this.debug && console.warn( `Panel element not found for ID: ${currentPanel.id}` );
        break;
      }

      panelElement.style.setProperty( '--header-panel-height', `${heightValue}px` );
      this.debug && console.info( `Set height on panel #${currentPanel.id}: ${heightValue}px` );

      // Move to parent panel
      currentPanel = currentPanel.parent
        ? this.panelHeights.find( item => item.id === currentPanel.parent )
        : null;
    }

    this.debug && console.info( 'panelHeights:', this.panelHeights );
    this.debug && console.groupEnd();
  }

  /**
   * Set the panel height CSS variable to 0.
   */
  removePanelHeight() {
    this.debug && console.info( 'Removing panel height' );
    document.body.style.setProperty( '--header-panel-height', '0px' );
  }

  /**
   * Close a panel by its ID.
   *
   * @param {string} id The ID of the panel to close.
   * @param {boolean} setFocus Whether to set focus back to the toggle button or previous panel.
   */
  closePanel(id, setFocus = false) {
    const panel = document.getElementById( id )
    if (!panel) {
      // Do nothing if the panel doesn't exist.
      return;
    }

    const activeClass = panel.getAttribute( 'data-toggle-panel-class' ) || 'is-active';
    panel.classList.remove( activeClass );

    this.updateToggles( id, false );
    this.updateTabIndexPerPanel( panel, false );

    const indexOffset = this.activePanels.indexOf( id );

    // Remove the panel ID from the active panels array.
    this.activePanels = this.activePanels.filter( panelId => panelId !== id );

    // Remove from panelHeights array as well
    this.activePanels[indexOffset - 1] && this.updateTabIndexPerPanel( this.activePanels[indexOffset - 1], true );

    this.removePanelHeight();

    const parentId = this.findParentPanel( id );
    if( parentId ) {
      document.getElementById( parentId ).style.removeProperty( '--header-panel-height' ); // NEED TO REMOVE FIRST TO FORCE RE-APPLY. Should be removed, but isn't for now.
      this.dispatchEvent( "setHeight", {id: parentId} );
    }

    if ('keydown' === this.lastInputType) {
      if (0 === this.activePanels.length && setFocus) {
        // Set focus back to the toggle button if no panels are left open.
        this.navigationLocations?.forEach( (navLocation) => {
          if (navLocation.contains( panel )) {
            const primaryToggle = navLocation?.querySelector( `[data-toggle-panel="${id}"]` );
            if (primaryToggle) {
              primaryToggle.focus();
            }
          }
        } );
      } else if (1 === this.activePanels.length && setFocus) {
        // Focus the toggle button if there's only one panel left open.
        document.getElementById( this.activePanels[0] )?.querySelector( `[data-toggle-panel="${id}"]` )?.focus();
      } else if (setFocus) {
        // Set focus to the first focusable element of the previous panel.
        this.setFocusElement( panel, true );
      }
    }
  }

  /**
   *
   * @param {number|undefined} depth
   */
  closePanelsFromSameDepth(depth) {
    if (typeof depth === 'undefined') {
      return;
    }

    this.debug && console.info( `Closing panels from depth: ${depth}` );

    // Get all panels with the same depth.
    this.activePanels?.filter( id => document.getElementById( id )?.getAttribute( 'data-panel-depth' ) === depth )?.forEach( id => this.closePanel( id, true ) );
  }

  /**
   * Update the aria-expanded attribute of buttons associated with a panel.
   *
   * @param {string} id The ID of the panel.
   * @param {boolean} state The state to set (true for expanded, false for collapsed).
   */
  updateToggles(id, state = false) {
    const buttonsToUpdate = document.querySelectorAll( `[data-toggle-panel="${id}"]` );
    if (!buttonsToUpdate?.length) {
      return;
    }

    buttonsToUpdate?.forEach( button => button.setAttribute( 'aria-expanded', state ? 'true' : 'false' ) );
  }

  /**
   * Update the tabindex of elements within the panel based on its active state.
   *
   * @param {HTMLElement} panel The panel element.
   * @param {boolean} enableOrDisable True to enable tabindex, false to disable.
   */
  updateTabIndexPerPanel(panel, enableOrDisable = false) {
    if (!panel) {
      return;
    }

    if (typeof panel === 'string') {
      // Just in case a string ID is passed instead of the element itself.
      this.updateTabIndexPerPanel( document.getElementById( panel ), enableOrDisable );
      return;
    }

    setTabIndex( this.getFocusableChildren( panel ), enableOrDisable ? 0 : -1 );
  }

  /**
   * Set focus to the first focusable element within a panel.
   * @param {HTMLElement|string} panel The panel element to search within.
   * @param {boolean} focusFirst True to focus the first element, false to focus the last element.
   */
  setFocusElement(panel, focusFirst = true) {
    if (typeof panel === 'string') {
      panel = document.getElementById( panel );
    }

    const el = panel?.querySelector( '.denhaag-header__panel--inner' ) || panel;

    const focusableChild = focusFirst
      ? this.getFirstFocusableChild( el )
      : this.getLastFocusableChild( el );

    if (focusableChild) {
      focusableChild.focus();
    }
  }

  /**
   * Get all focusable elements within a parent element.
   *
   * @param parent
   * @return {*|*[]}
   */
  getFocusableChildren(parent) {
    if (!parent) {
      return [];
    }
    // Check if there's an inner panel wrapper and use that as the parent instead.
    parent = parent?.querySelector( '.denhaag-header__panel' ) || parent;

    return parent?.querySelectorAll( this.focusableSelectors.join( ',' ) ) || [];
  }

  /**
   * Get the first focusable child element within a parent element.
   * @param {HTMLElement} parent The parent element to search within.
   * @return {HTMLElement|null} The first focusable child element, or null if none found.
   */
  getFirstFocusableChild(parent) {
    return this.getFocusableElements( parent );
  }

  /**
   * Get the first focusable element within a parent element.
   * @param {HTMLElement} parent The parent element.
   * @param {boolean} reverse Reverse the order of elements to find the last focusable element.
   * @return {HTMLElement|null} The first/last focusable element, or null if none found.
   */
  getFocusableElements(parent, reverse = false) {
    if (!parent) {
      return null;
    }

    if (typeof panel === 'string') {
      parent = document.getElementById( parent );
    }

    let elements = this.getFocusableChildren( parent );

    if (!elements?.length) {
      return null;
    }

    if (reverse) {
      elements = Array.from( elements ).reverse();
    }

    for (const element of elements) {
      if (this.isElementFocusable( element )) {
        return element;
      }
    }

    return null;
  }

  /**
   * Get the last focusable child element within a parent element.
   * @param {HTMLElement} parent The parent element to search within.
   * @return {HTMLElement|null} The last focusable child element, or null if none found.
   */
  getLastFocusableChild(parent) {
    return this.getFocusableElements( parent, true );
  }

  /**
   * Check if an element is focusable.
   *
   * @param {HTMLElement} element The element to check.
   * @return {boolean} True if the element is focusable, false otherwise.
   */
  isElementFocusable(element) {
    // Check if element is visible and not disabled
    const style = window.getComputedStyle( element );

    return (
      element.offsetWidth > 0 &&
      element.offsetHeight > 0 &&
      style.visibility !== 'hidden' &&
      style.display !== 'none' &&
      !element.hasAttribute( 'disabled' ) &&
      element.tabIndex !== -1
    );
  }

  /**
   * Get a CSS variable value and parse it as an integer.
   * @param {string} varName The name of the CSS variable (e.g., '--my-variable').
   * @param {number} defaultValue The default value to return if the variable is not found or invalid.
   * @return {number}
   */
  getCSSVariable(varName, defaultValue = 0) {
    if (typeof varName !== 'string') {
      return defaultValue;
    }

    const value = getComputedStyle( document.body ).getPropertyValue( varName );

    if (value && value.includes( 'calc(' )) {
      // CSS variables are computed to `calc(3rem + 0.75rem)` etc. We need to extract the numeric part.

      // Do the calc and return the computed value in pixels.
      const tempElement = document.createElement( 'div' );
      tempElement.style.width = value;
      document.body.appendChild( tempElement );
      const computedValue = window.getComputedStyle( tempElement ).width;
      document.body.removeChild( tempElement );
      return parseInt( computedValue.trim(), 10 );
    }

    return value ? parseInt( value.trim(), 10 ) : defaultValue;
  }
}
